import { useState, useMemo } from "react";
// @ts-ignore
import CheckmarkBold from "svg/checkmark-bold.svg";
import Button from "components/Button";
import ProductCard from "components/ProductCard";
import { useArrangement } from "./ArrangementContext";
import UrnDetailModal from "./UrnDetailModal";
import { formatVariantLabel } from "./variantLabels";
import type { UrnProduct } from "./types";
import { useExperimentVariant } from "hooks/useExperimentVariant";

const EXPANDED_URNS_FLAG = "at-need-expanded-urns";

export default function UrnSelection() {
  const { state, setAdditionalUrn, clearAdditionalUrn, config, isReadOnly } =
    useArrangement();
  const [modalUrn, setModalUrn] = useState<UrnProduct | null>(null);

  const variant = useExperimentVariant(EXPANDED_URNS_FLAG, isReadOnly);
  const isExpandedTest = !isReadOnly && variant === "test";

  // Experiment gating: urns tagged cohort-test are visible only to the "test"
  // cohort (selected ones always stay visible). Delete with the experiment.
  const sortedUrns = useMemo(() => {
    const urns = config?.urns ?? [];
    return urns
      .filter(
        (u) =>
          isExpandedTest ||
          !u.cohortTest ||
          state.additionalUrns.some((s) => s.urnId === u.id),
      )
      .sort((a, b) => {
        if (a.popular && !b.popular) return -1;
        if (!a.popular && b.popular) return 1;
        return 0;
      });
  }, [config?.urns, isExpandedTest, state.additionalUrns]);

  return (
    <div>
      <div className="grid grid-cols-1 justify-items-center gap-20 sm:grid-cols-2 md:grid-cols-3">
        {sortedUrns.map((urn) => {
          const selection = state.additionalUrns.find(
            (u) => u.urnId === urn.id,
          );
          const isSelected = !!selection;
          const selectedVariant = selection
            ? urn.variants.find((v) => v.id === selection.variantId)
            : null;
          const selectedVariantLabel = formatVariantLabel(
            selectedVariant?.name,
          );

          return (
            <ProductCard
              key={urn.id}
              name={urn.name}
              price={urn.price}
              image={selectedVariant?.image || urn.image}
              selected={isSelected}
            >
              {isSelected ? (
                <div className="space-y-2">
                  <Button
                    onClick={() => setModalUrn(urn)}
                    variant="text"
                    fullWidth
                  >
                    <span className="flex items-center justify-center gap-1.5">
                      <CheckmarkBold className="h-4 w-4" />
                      Selected
                      {selectedVariantLabel ? ` · ${selectedVariantLabel}` : ""}
                      {` · Qty ${selection?.quantity}`}
                    </span>
                  </Button>
                  {!isReadOnly && (
                    <Button
                      onClick={() => clearAdditionalUrn(urn.id)}
                      variant="outline"
                      size="sm"
                      fullWidth
                    >
                      Remove
                    </Button>
                  )}
                </div>
              ) : isReadOnly ? null : (
                <Button
                  onClick={() => setModalUrn(urn)}
                  variant="tertiary"
                  fullWidth
                >
                  View details
                </Button>
              )}
            </ProductCard>
          );
        })}
      </div>

      <UrnDetailModal
        urn={modalUrn}
        isOpen={!!modalUrn}
        onClose={() => setModalUrn(null)}
        onSelect={setAdditionalUrn}
        readOnly={isReadOnly}
        currentVariantId={
          modalUrn
            ? (state.additionalUrns.find((u) => u.urnId === modalUrn.id)
                ?.variantId ?? null)
            : null
        }
        currentQuantity={
          modalUrn
            ? state.additionalUrns.find((u) => u.urnId === modalUrn.id)
                ?.quantity
            : undefined
        }
        currentEngraving={
          modalUrn
            ? state.additionalUrns.find((u) => u.urnId === modalUrn.id)
                ?.engraving
            : undefined
        }
      />
    </div>
  );
}
